// src/antdviews/LoginView.tsx

// 登录页面 - Ant Design 重构版

import React from 'react'
import localStorage from 'localStorage'
import { withTranslation, WithTranslation } from 'react-i18next'
import axios from 'axios'
import {
    Form,
    Input,
    Button,
    Radio,
    Checkbox,
    Select,
    Modal,
    Row,
    Col,
    Card,
    Typography,
    Space,
    Alert,
} from 'antd'
import {
    UserOutlined,
    LockOutlined,
    GlobalOutlined,
    LoginOutlined,
    ReloadOutlined,
    WarningOutlined,
} from '@ant-design/icons'
import { AuthApi, ROOT } from '../services/Api'
import { ROLE } from '../services/Macro'
import { Global } from '../services/Global'
import { browserUtil } from '../utils/BrowserUtil'
import { encrypUtil } from '../utils/EncrypUtil'
import { Page } from '../services/Page'
// import '../css/login.css'

const { Title, Text } = Typography
const { Option } = Select

const ROLES = {
    super: { value: ROLE.SUPER, label: '管理员' },
    agent: { value: ROLE.AGENT, label: '代理商' },
    user: { value: ROLE.USER, label: '普通用户' },
}

interface LoginViewState {
    username: string
    password: string
    lang: string
    selectedRole: { value: number; label: string }
    warnTitle: string
    warnInfo: string
    rememberMe: boolean
    showModal: boolean
    loginDisabled: boolean
    remainTime: number
    loading: boolean
}

class LoginView extends React.Component<WithTranslation, LoginViewState> {

    private intervalId: NodeJS.Timeout | null = null

    constructor(props: WithTranslation) {
        super(props)
        this.state = {
            username: localStorage.getItem('username') || '',
            password: localStorage.getItem('password') || '',
            lang: 'zh_CN',
            selectedRole: ROLES.user,
            warnTitle: '',
            warnInfo: '',
            rememberMe: localStorage.getItem('rememberMe') === '1',
            showModal: false,
            loginDisabled: false,
            remainTime: 0,
            loading: false,
        }
    }

    componentWillMount() {
        Global.isMobile = browserUtil.checkMobile()
    }

    componentDidMount() {
        const DEBUG = false
        const TAG = `LoginView.componentDidMount() - `
        // 检测"记住账号"是否勾选
        const rememberMe = localStorage.getItem('rememberMe')
        DEBUG && console.log(TAG, `rememberMe:`, rememberMe)
        this.setState({ rememberMe: rememberMe === '1' })

        // 初始化用户角色
        let role = localStorage.getItem('role')
        DEBUG && console.log(TAG, `role:`, role)
        if (role) {
            DEBUG && console.log(TAG, `改变角色`)
            const roleNum = Number(role)
            switch (roleNum) {
                case ROLE.SUPER:
                    DEBUG && console.log(TAG, `角色变为超级管理员`)
                    this.setState({ selectedRole: ROLES.super })
                    break
                case ROLE.USER:
                    DEBUG && console.log(TAG, `角色变为普通用户`)
                    this.setState({ selectedRole: ROLES.user })
                    break
            }
        }

        // 初始化语言选项
        const lang = localStorage.getItem('lang') || 'zh_CN'
        this.changeLanguage(lang)
    }

    componentWillUnmount() {
        if (this.intervalId) {
            clearInterval(this.intervalId)
            this.intervalId = null
        }
    }

    changeLanguage = (lang: string) => {
        const { i18n } = this.props
        i18n.changeLanguage(lang)
        this.setState({ lang })
    }

    openModal = () => {
        this.setState({ showModal: true })
    }

    closeModal = () => {
        this.setState({ showModal: false })
    }

    loginSuper = async (t: any) => {
        const DEBUG = false
        const TAG = `LoginView.loginSuper() - `
        DEBUG && console.log(TAG, `超级管理员登录`)
        this.setState({ loading: true })
        try {
            const requestData = {
                username: this.state.username,
                password: this.state.password,
                role: ROLE.SUPER,
                lang: this.state.lang.split('_')[0],
            }
            const params = await encrypUtil.encodeRequestRsa(requestData)
            const response = await axios.post(AuthApi.SUPER_LOGIN, params)
            DEBUG && console.log(TAG, `response.data:`, response.data)
            if (400 === response.data.code) {
                this.setState({
                    warnTitle: t('serverError'),
                    warnInfo: response.data.message,
                })
                this.openModal()
                return
            }
            if (200 === response.data.code) {
                localStorage.setItem('token', response.data.data.token)
                localStorage.setItem('role', String(ROLE.SUPER))
                window.location.href = `${ROOT}/${Page.accountMgmt}`
            }
        } finally {
            this.setState({ loading: false })
        }
    }

    loginAgent = async (t: any) => {
        // TODO
        this.setState({ loading: false })
    }

    loginUser = async (t: any) => {
        const DEBUG = true
        const TAG = `LoginView.loginUser() - `
        DEBUG && console.log(TAG, `普通用户登录`)
        this.setState({ loading: true })
        try {
            const requestData = {
                email: this.state.username,
                password: this.state.password,
                role: ROLE.USER,
                lang: this.state.lang.split('_')[0],
            }
            const params = await encrypUtil.encodeRequestRsa(requestData)
            const response = await axios.post(AuthApi.USER_LOGIN, params)
            DEBUG && console.log(TAG, `response.data:`, response.data)
            if (400 === response.data.code) {
                this.setState({
                    warnTitle: t('serverError'),
                    warnInfo: response.data.message,
                })
                this.openModal()
                if (response.data.errCode === 10058) {
                    const remain = response.data.extraData?.remain || 0
                    if (remain > 0) {
                        DEBUG && console.log(TAG, `remain:`, remain)
                        this.setState({ loginDisabled: true, remainTime: remain })
                        this.intervalId = setInterval(() => {
                            const time = this.state.remainTime - 1
                            if (time <= 0) {
                                this.setState({ loginDisabled: false, remainTime: 0 })
                                if (this.intervalId) {
                                    clearInterval(this.intervalId)
                                    this.intervalId = null
                                }
                            } else {
                                this.setState({ remainTime: time })
                            }
                        }, 1000)
                    }
                }
                return
            }
            if (200 === response.data.code) {
                localStorage.setItem('token', response.data.data.token)
                localStorage.setItem('role', String(ROLE.USER))
                window.location.href = `${ROOT}/userAccountMgmt`
            }
        } finally {
            this.setState({ loading: false })
        }
    }

    saveAccountInfo() {
        localStorage.setItem('username', this.state.username)
        localStorage.setItem('password', this.state.password)
        localStorage.setItem('rememberMe', '1')
    }

    removeAccountInfo() {
        localStorage.removeItem('username')
        localStorage.removeItem('password')
        localStorage.removeItem('rememberMe')
    }

    handleReset = () => {
        const DEBUG = false
        const TAG = `LoginView.render().handleReset() - `
        DEBUG && console.log(TAG, '清空表单')
        this.setState({ username: '', password: '' })
    }

    gotoResetPwd = () => {
        const DEBUG = false
        const TAG = `LoginView.render().gotoResetPwd() - `
        DEBUG && console.log(TAG, '跳转到重置密码页面')
        window.location.href = `${ROOT}/${Page.reset}`
    }

    handleLogin = async () => {
        const DEBUG = false
        const TAG = `LoginView.render().handleLogin() - `
        const { t } = this.props
        if (!this.state.password) {
            DEBUG && console.log(TAG, '密码为空')
            this.setState({
                warnTitle: t('localError'),
                warnInfo: t('emptyPassword'),
            })
            this.openModal()
            return
        }
        if (this.state.rememberMe) {
            this.saveAccountInfo()
        }
        DEBUG && console.log(TAG, this.state.username, this.state.password, '发送后端验证')
        if (this.state.selectedRole.value === ROLE.SUPER) {
            await this.loginSuper(t)
        } else if (this.state.selectedRole.value === ROLE.AGENT) {
            await this.loginAgent(t)
        } else if (this.state.selectedRole.value === ROLE.USER) {
            await this.loginUser(t)
        }
    }

    handleRememberMe = (e: any) => {
        const checked = e.target.checked
        this.setState({ rememberMe: checked })
        if (checked) {
            this.saveAccountInfo()
        } else {
            this.removeAccountInfo()
        }
    }

    handleRadioChange = (e: any) => {
        const DEBUG = false
        const TAG = `LoginView.render().handleRadioChange() - `
        const role = e.target.value
        DEBUG && console.log(TAG, `role:`, role)
        if (role === ROLE.SUPER) {
            this.setState({ selectedRole: ROLES.super })
        } else if (role === ROLE.USER) {
            this.setState({ selectedRole: ROLES.user })
        }
    }

    handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ username: e.target.value })
    }

    handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ password: e.target.value })
    }

    render() {
        const { t } = this.props
        // const { options } = this.state
        const lang = localStorage.getItem('lang') || 'zh_CN'
        const selectedLang = lang === 'en_US' ? 'en_US' : 'zh_CN'

        return (
            <div className="login" style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f0f2f5'
            }}>
                {/* 警告弹窗 */}
                <Modal
                    title={
                        <span>
                            <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
                            {t('error')}
                        </span>
                    }
                    open={this.state.showModal}
                    onCancel={this.closeModal}
                    footer={[
                        <Button key="confirm" type="primary" onClick={this.closeModal}>
                            {t('confirm')}
                        </Button>,
                    ]}
                    centered
                >
                    <Text type="danger">{this.state.warnInfo}</Text>
                </Modal>

                <Card
                    style={{
                        width: 420,
                        maxWidth: '96%',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        borderRadius: 8,
                    }}
                    bodyStyle={{ padding: '32px 40px' }}
                >
                    {/* 顶部：标题 + 语言切换 */}
                    <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                        <Col>
                            <Title level={3} style={{ margin: 0 }}>
                                <UserOutlined style={{ marginRight: 8 }} />
                                {t('login')}
                            </Title>
                        </Col>
                        <Col>
                            <Select
                                value={selectedLang}
                                onChange={(value: string) => {
                                    localStorage.setItem('lang', value)
                                    this.setState({ lang: value })
                                    this.changeLanguage(value)
                                }}
                                style={{ width: 120 }}
                                suffixIcon={<GlobalOutlined />}
                            >
                                <Option value="zh_CN">简体中文</Option>
                                <Option value="en_US">English</Option>
                            </Select>
                        </Col>
                    </Row>

                    <Form layout="vertical" onFinish={this.handleLogin}>
                        {/* 账号输入 */}
                        <Form.Item
                            label={t('accountEmail')}
                            required={false}
                            rules={[{ required: true, message: t('emptyAccount') }]}
                        >
                            <Input
                                prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                                placeholder={t('accountEmail')}
                                value={this.state.username}
                                onChange={this.handleUsernameChange}
                                size="large"
                            />
                        </Form.Item>

                        {/* 密码输入 */}
                        <Form.Item
                            label={t('password')}
                            required={false}
                            rules={[{ required: true, message: t('emptyPassword') }]}
                        >
                            <Input.Password
                                prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                                placeholder={t('password')}
                                value={this.state.password}
                                onChange={this.handlePasswordChange}
                                size="large"
                            />
                        </Form.Item>

                        {/* 角色选择（单选按钮） */}
                        <Form.Item label={t('role')}>
                            <Radio.Group
                                value={this.state.selectedRole.value}
                                onChange={this.handleRadioChange}
                            >
                                <Radio value={ROLE.USER}>{t('normalUser')}</Radio>
                                <Radio value={ROLE.SUPER}>{t('admin')}</Radio>
                            </Radio.Group>
                        </Form.Item>

                        {/* 操作区域 */}
                        {this.renderOpArea(t)}
                    </Form>
                </Card>
            </div>
        )
    }

    renderOpArea(t: any) {
        if (this.state.loginDisabled) {
            return (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Checkbox
                        checked={this.state.rememberMe}
                        onChange={this.handleRememberMe}
                    >
                        {t('rememberMe')}
                    </Checkbox>
                    <Button
                        type="primary"
                        block
                        size="large"
                        disabled
                        icon={<LoginOutlined />}
                    >
                        {`${t('login')} (${this.state.remainTime}s)`}
                    </Button>
                    <Button
                        block
                        size="large"
                        onClick={this.gotoResetPwd}
                    >
                        {t('resetPwd')}
                    </Button>
                </Space>
            )
        }

        return (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Checkbox
                    checked={this.state.rememberMe}
                    onChange={this.handleRememberMe}
                >
                    {t('rememberMe')}
                </Checkbox>
                <Row gutter={12}>
                    <Col span={12}>
                        <Button
                            type="primary"
                            block
                            size="large"
                            loading={this.state.loading}
                            onClick={this.handleLogin}
                            icon={<LoginOutlined />}
                        >
                            {t('login')}
                        </Button>
                    </Col>
                    <Col span={12}>
                        <Button
                            block
                            size="large"
                            onClick={this.handleReset}
                            icon={<ReloadOutlined />}
                        >
                            {t('reset')}
                        </Button>
                    </Col>
                </Row>
                <Button
                    block
                    size="large"
                    onClick={this.gotoResetPwd}
                >
                    {t('resetPwd')}
                </Button>
            </Space>
        )
    }
}

export const LoginPage = withTranslation()(LoginView)